import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';
import {
   getFinancialPostingCashierId,
   getFinancialPostingDate,
   getFinancialPostingGuestName,
   getFinancialPostingReason,
   getFinancialPostingRoom,
   getFinancialPostingSource,
   getFinancialPostingTime,
   getFinancialPostingTransactionCode,
   getFinancialPostingUserDisplay
} from '../utils/financial-posting.utils';
import { getFirstNumber, getFirstString } from '../utils/report-field.utils';
import {
   formatCurrency,
   formatFlexibleDate,
   formatTime
} from '../utils/report-formatting.utils';
import { getRealTyallsRowId } from '../utils/report-row-id.utils';

const LAYOUT_SUMMARY = 'summary';
const LAYOUT_DETAILED_ADJUSTMENTS = 'detailed-adjustments';
const LAYOUT_ADJUSTMENTS_BY_USER = 'adjustments-by-user';

type RevenueAdjustmentRow = {
   rowId: string;
   date: string;
   dateRaw: string;
   time: string;
   room: string;
   guestName: string;
   transaction: string;
   transactionCode: string;
   transactionDescription: string;
   reason: string;
   amount: string;
   amountRaw: number;
   user: string;
   cashierId: string;
   hasRealTyallsRowId: boolean;
};

type AdjustmentUserSummary = {
   user: string;
   count: number;
   totalAmount: number;
   sourceRowIds: string[];
};

function getRowId(row: unknown): string {
   const realTyallsRowId = getRealTyallsRowId(row);

   if (realTyallsRowId) {
      return realTyallsRowId;
   }

   return (
      getFirstString(row, [
         'TransactionNo',
         'transactionNo',
         'ReceiptNo',
         'receiptNo',
         'Id',
         'id'
      ]) ||
      `revenue-adjustment:${getFinancialPostingDate(
         row
      )}:${getFinancialPostingTime(row)}:${getFinancialPostingRoom(
         row
      )}:${getFinancialPostingTransactionCode(row)}`
   );
}

function amountFromMappedPostingDollars(row: unknown): number | null {
   const directAmount = getFirstNumber(row, [
      'CreditAdjustment',
      'creditAdjustment',
      'DebitRevenue',
      'debitRevenue',
      'Amount',
      'amount',
      'AmountRaw',
      'amountRaw',
      'AdjustmentAmount',
      'adjustmentAmount'
   ]);

   if (directAmount !== null && directAmount < 0) {
      return directAmount;
   }

   const debitCents = getFirstNumber(row, [
      'DebitAmount.Amount',
      'debitAmount.amount',
      'DebitAmount',
      'debitAmount'
   ]);

   if (debitCents !== null && debitCents < 0) {
      return debitCents / 100;
   }

   const postedCents = getFirstNumber(row, [
      'PostedAmount.Amount',
      'postedAmount.amount',
      'PostedAmount',
      'postedAmount'
   ]);

   if (postedCents !== null && postedCents < 0) {
      return Math.abs(postedCents) > 999 ? postedCents / 100 : postedCents;
   }

   return null;
}

function amountFromOraclePostingDollars(row: unknown): number | null {
   const debitAmount = getFirstNumber(row, [
      'debitAmount.amount',
      'DebitAmount.Amount',
      'DebitAmount',
      'debitAmount'
   ]);

   if (debitAmount !== null && debitAmount < 0) {
      return debitAmount;
   }

   const postedAmount = getFirstNumber(row, [
      'postedAmount.amount',
      'amount.amount',
      'PostedAmount.Amount',
      'PostedAmount',
      'Amount',
      'amount'
   ]);

   if (postedAmount !== null && postedAmount < 0) {
      return postedAmount;
   }

   return null;
}

function getTransactionDescription(row: unknown): string {
   return getFirstString(row, [
      'TransactionDescription',
      'transactionDescription',
      'TransactionCodeDescription',
      'transactionCodeDescription',
      'CodeDescription',
      'codeDescription',
      'Description',
      'description'
   ]);
}

function getTransactionDisplay(row: unknown): string {
   const code = getFinancialPostingTransactionCode(row);
   const description = getTransactionDescription(row);

   if (code && description) {
      return `${description} (${code})`;
   }

   return description || code;
}

function getReason(row: unknown): string {
   return getFinancialPostingReason(row, [
      [
         'Reason',
         'reason',
         'DescriptionReference',
         'descriptionReference',
         'TransactionRemark',
         'transactionRemark',
         'Remark',
         'remark',
         'Reference',
         'reference'
      ],
      ['Description', 'description']
   ]);
}

function normaliseAdjustmentRows(
   sourceRows: unknown[],
   isOracle: boolean
): RevenueAdjustmentRow[] {
   const rows: RevenueAdjustmentRow[] = [];

   for (const sourceRow of sourceRows) {
      const amountRaw = isOracle
         ? amountFromOraclePostingDollars(sourceRow)
         : amountFromMappedPostingDollars(sourceRow);

      if (amountRaw === null || amountRaw >= 0) {
         continue;
      }

      const dateRaw = getFinancialPostingDate(sourceRow);
      const transactionCode = getFinancialPostingTransactionCode(sourceRow);

      rows.push({
         rowId: getRowId(sourceRow),
         date: formatFlexibleDate(dateRaw),
         dateRaw,
         time: formatTime(getFinancialPostingTime(sourceRow)),
         room: getFinancialPostingRoom(sourceRow),
         guestName: getFinancialPostingGuestName(sourceRow),
         transaction: getTransactionDisplay(sourceRow),
         transactionCode,
         transactionDescription: getTransactionDescription(sourceRow),
         reason: getReason(sourceRow),
         amount: formatCurrency(amountRaw),
         amountRaw,
         user: getFinancialPostingUserDisplay(sourceRow),
         cashierId: getFinancialPostingCashierId(sourceRow),
         hasRealTyallsRowId: !!getRealTyallsRowId(sourceRow)
      });
   }

   return rows;
}

function sortAdjustmentRows(rows: RevenueAdjustmentRow[]): RevenueAdjustmentRow[] {
   return [...rows].sort((a, b) => {
      const dateCompare = a.dateRaw.localeCompare(b.dateRaw);

      if (dateCompare !== 0) {
         return dateCompare;
      }

      return a.time.localeCompare(b.time);
   });
}

function hasBackendRisk(row: unknown): boolean {
   const sourceRow = row as any;

   return (
      sourceRow?.HasRisk === true ||
      (Array.isArray(sourceRow?.Risks) && sourceRow.Risks.length > 0)
   );
}

function getAdjustmentRows(
   reportJson: unknown,
   backendRiskOnly = false
): RevenueAdjustmentRow[] {
   const { rows: sourceRows, isOracle } = getFinancialPostingSource(reportJson, [
      'PmsRevenueAdjustments',
      'PMSRevenueAdjustments',
      'RevenueAdjustments'
   ]);

   const selectedRows = backendRiskOnly
      ? sourceRows.filter((row) => hasBackendRisk(row))
      : sourceRows;

   return sortAdjustmentRows(normaliseAdjustmentRows(selectedRows, isOracle));
}

function buildAdjustmentUserSummaries(
   rows: RevenueAdjustmentRow[]
): AdjustmentUserSummary[] {
   const totals = new Map<string, AdjustmentUserSummary>();

   for (const row of rows) {
      const user = row.user || 'Unknown';

      const current = totals.get(user) ?? {
         user,
         totalAmount: 0,
         count: 0,
         sourceRowIds: []
      };

      current.totalAmount += row.amountRaw;
      current.count += 1;

      if (row.hasRealTyallsRowId) {
         current.sourceRowIds.push(row.rowId);
      }

      totals.set(user, current);
   }

   return Array.from(totals.values()).sort((a, b) => {
      const aNumber = Number(a.user);
      const bNumber = Number(b.user);

      if (Number.isFinite(aNumber) && Number.isFinite(bNumber)) {
         return aNumber - bNumber;
      }

      return a.user.localeCompare(b.user);
   });
}

function flattenAdjustmentRows(
   reportJson: unknown,
   backendRiskOnly: boolean
): ReportRowVm[] {
   const adjustmentRows = getAdjustmentRows(reportJson, backendRiskOnly);

   return adjustmentRows.map((row) => ({
      rowId: row.rowId,
      isAnnotatable: row.hasRealTyallsRowId,
      cells: {
         rowType: 'transaction',
         date: row.date,
         dateRaw: row.dateRaw,
         time: row.time,
         room: row.room,
         guestName: row.guestName,
         transaction: row.transaction,
         transactionCode: row.transactionCode,
         transactionDescription: row.transactionDescription,
         reason: row.reason,
         amount: row.amount,
         amountRaw: row.amountRaw,
         user: row.user,
         cashierId: row.cashierId
      }
   }));
}

function flattenDetailedAdjustments(reportJson: unknown): ReportRowVm[] {
   return flattenAdjustmentRows(reportJson, false);
}

function flattenRiskAdjustments(reportJson: unknown): ReportRowVm[] {
   return flattenAdjustmentRows(reportJson, true);
}

function flattenAdjustmentsByUser(reportJson: unknown): ReportRowVm[] {
   const adjustmentRows = getAdjustmentRows(reportJson);
   const userSummaries = buildAdjustmentUserSummaries(adjustmentRows);

   return userSummaries.map((summary) => ({
      rowId: `pms-revenue-adjustments:${LAYOUT_ADJUSTMENTS_BY_USER}:user:${summary.user}`,
      sourceRowIds: Array.from(new Set(summary.sourceRowIds)),
      isAnnotatable: false,
      cells: {
         rowType: 'userAdjustmentSummary',
         user: summary.user,
         userRaw: summary.user,
         count: summary.count,
         totalAdjusted: formatCurrency(summary.totalAmount),
         totalAdjustedRaw: summary.totalAmount
      }
   }));
}

export const PMS_REVENUE_ADJUSTMENTS_DEFINITION: ReportDefinition = {
   reportType: 'PmsRevenueAdjustments',
   version: 7,
   title: 'PMS Revenue Adjustments',

   sorting: {
      enabled: true,
      persist: true
   },

   header: {
      title: 'Revenue Adjustments'
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      selectableRowTypes: ['transaction']
   },

   annotations: false,

   columns: [
      {
         key: 'date',
         header: 'TRN DATE',
         width: '105px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['dateRaw']
         }
      },
      {
         key: 'room',
         header: 'ROOM',
         width: '80px',
         sort: { type: 'string' }
      },
      {
         key: 'guestName',
         header: 'GUEST NAME',
         width: '160px',
         sort: { type: 'string' }
      },
      {
         key: 'transaction',
         header: 'TRANSACTION',
         width: '180px',
         sort: { type: 'string' }
      },
      {
         key: 'reason',
         header: 'REASON',
         width: '360px',
         maxChars: 130,
         sort: { type: 'string' }
      },
      {
         key: 'amount',
         header: 'AMOUNT',
         width: '115px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['amountRaw'],
            defaultDirection: 'asc'
         }
      },
      {
         key: 'user',
         header: 'USER',
         width: '140px',
         sort: { type: 'string' }
      }
   ],

   filter: {
      fields: [
         {
            key: 'date',
            label: 'Transaction Date',
            type: 'date',
            getValue: (row) => row.cells['dateRaw']
         },
         { key: 'room', label: 'Room', type: 'string' },
         { key: 'guestName', label: 'Guest Name', type: 'string' },
         { key: 'transaction', label: 'Transaction', type: 'string' },
         { key: 'reason', label: 'Reason', type: 'string' },
         {
            key: 'amount',
            label: 'Amount',
            type: 'number',
            getValue: (row) => row.cells['amountRaw']
         },
         { key: 'user', label: 'User', type: 'string' }
      ]
   },

   flattenRows: flattenDetailedAdjustments,

   layouts: [
      {
         id: LAYOUT_SUMMARY,
         label: 'Summary',
         annotations: {
            enabled: true,
            selectableRowTypes: ['transaction']
         },
         rowChecks: {
            enabled: false,
            includeNestedRows: false,
            selectableRowTypes: ['transaction']
         },
         columns: [
            {
               key: 'date',
               header: 'TRN DATE',
               width: '105px',
               sort: {
                  type: 'date',
                  getValue: (row) => row.cells['dateRaw']
               }
            },
            {
               key: 'room',
               header: 'ROOM',
               width: '80px',
               sort: { type: 'string' }
            },
            {
               key: 'guestName',
               header: 'GUEST NAME',
               width: '160px',
               sort: { type: 'string' }
            },
            {
               key: 'transaction',
               header: 'TRANSACTION',
               width: '180px',
               sort: { type: 'string' }
            },
            {
               key: 'reason',
               header: 'REASON',
               width: '360px',
               maxChars: 130,
               sort: { type: 'string' }
            },
            {
               key: 'amount',
               header: 'AMOUNT',
               width: '115px',
               sort: {
                  type: 'number',
                  getValue: (row) => row.cells['amountRaw'],
                  defaultDirection: 'asc'
               }
            },
            {
               key: 'user',
               header: 'USER',
               width: '140px',
               sort: { type: 'string' }
            }
         ],
         flattenRows: flattenRiskAdjustments
      },
      {
         id: LAYOUT_DETAILED_ADJUSTMENTS,
         label: 'Detailed Adjustments',
         annotations: {
            enabled: true,
            selectableRowTypes: ['transaction'],
            disabledMessage:
               'Row comments and row highlighting are not available for this report layout. Please use the main report comments panel to add comments for this report.'
         },
         rowChecks: {
            enabled: false,
            includeNestedRows: false,
            selectableRowTypes: ['transaction']
         },
         columns: [
            {
               key: 'date',
               header: 'TRN DATE',
               width: '105px',
               sort: {
                  type: 'date',
                  getValue: (row) => row.cells['dateRaw']
               }
            },
            {
               key: 'room',
               header: 'ROOM',
               width: '80px',
               sort: { type: 'string' }
            },
            {
               key: 'guestName',
               header: 'GUEST NAME',
               width: '160px',
               sort: { type: 'string' }
            },
            {
               key: 'transaction',
               header: 'TRANSACTION',
               width: '180px',
               sort: { type: 'string' }
            },
            {
               key: 'reason',
               header: 'REASON',
               width: '360px',
               maxChars: 130,
               sort: { type: 'string' }
            },
            {
               key: 'amount',
               header: 'AMOUNT',
               width: '115px',
               sort: {
                  type: 'number',
                  getValue: (row) => row.cells['amountRaw'],
                  defaultDirection: 'asc'
               }
            },
            {
               key: 'user',
               header: 'USER',
               width: '140px',
               sort: { type: 'string' }
            }
         ],
         filter: {
            fields: [
               {
                  key: 'date',
                  label: 'Transaction Date',
                  type: 'date',
                  getValue: (row) => row.cells['dateRaw']
               },
               { key: 'room', label: 'Room', type: 'string' },
               { key: 'guestName', label: 'Guest Name', type: 'string' },
               { key: 'transaction', label: 'Transaction', type: 'string' },
               { key: 'reason', label: 'Reason', type: 'string' },
               {
                  key: 'amount',
                  label: 'Amount',
                  type: 'number',
                  getValue: (row) => row.cells['amountRaw']
               },
               { key: 'user', label: 'User', type: 'string' }
            ]
         },
         flattenRows: flattenDetailedAdjustments
      },
      {
         id: LAYOUT_ADJUSTMENTS_BY_USER,
         label: 'Adjustments by User',
         annotations: {
            enabled: false,
            disabledMessage:
               'Row comments and row highlighting are not available for Adjustments by User because this layout is calculated from multiple rows. Please use the main report comments panel to add comments for this report.'
         },
         rowChecks: {
            enabled: false,
            includeNestedRows: false,
            selectableRowTypes: ['userAdjustmentSummary']
         },
         columns: [
            {
               key: 'user',
               header: 'USER',
               width: '220px',
               sort: {
                  type: 'string',
                  getValue: (row) => row.cells['userRaw']
               }
            },
            {
               key: 'count',
               header: 'COUNT',
               width: '120px',
               sort: {
                  type: 'number',
                  defaultDirection: 'desc'
               }
            },
            {
               key: 'totalAdjusted',
               header: 'TOTAL ADJUSTED',
               width: '180px',
               sort: {
                  type: 'number',
                  getValue: (row) => row.cells['totalAdjustedRaw'],
                  defaultDirection: 'asc'
               }
            }
         ],
         filter: {
            fields: [
               {
                  key: 'user',
                  label: 'User',
                  type: 'string',
                  getValue: (row) => row.cells['userRaw']
               },
               {
                  key: 'count',
                  label: 'Count',
                  type: 'number'
               },
               {
                  key: 'totalAdjusted',
                  label: 'Total Adjusted',
                  type: 'number',
                  getValue: (row) => row.cells['totalAdjustedRaw']
               }
            ]
         },
         flattenRows: flattenAdjustmentsByUser
      }
   ]
};
